<template>
    <!-- Info Button -->
    <button
        @click="showModal = true"
        :class="buttonClasses"
        :aria-label="ariaLabel"
        type="button"
    >
        <InformationCircleIcon :class="iconSize" />
    </button>

    <!-- Info Modal -->
    <Modal
        :show="showModal"
        @close="showModal = false"
        :title="title"
        :size="modalSize"
    >
        <div class="space-y-4 text-sm text-secondary-600 dark:text-secondary-400">
            <slot />
        </div>
    </Modal>
</template>

<script setup>
import { ref, computed } from 'vue';
import Modal from '@/Components/Common/Modal.vue';
import { InformationCircleIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
    // Modal title
    title: {
        type: String,
        default: 'Information',
    },
    // Button variant (style)
    variant: {
        type: String,
        default: 'default',
        validator: (value) => ['default', 'primary', 'subtle', 'white'].includes(value),
    },
    // Icon size
    size: {
        type: String,
        default: 'md',
        validator: (value) => ['sm', 'md', 'lg'].includes(value),
    },
    // Modal size
    modalSize: {
        type: String,
        default: 'lg',
        validator: (value) => ['sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl'].includes(value),
    },
    // Aria label for accessibility
    ariaLabel: {
        type: String,
        default: 'Show information',
    },
});

const showModal = ref(false);

// Button styling based on variant
const buttonClasses = computed(() => {
    const variants = {
        // Default - gray/neutral
        default: 'p-2 text-secondary-500 hover:text-secondary-700 dark:text-secondary-400 dark:hover:text-secondary-200 rounded-full hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors',

        // Primary - colored background
        primary: 'p-2 text-white/80 hover:text-white rounded-full hover:bg-white/10 transition-colors',

        // Subtle - minimal styling
        subtle: 'p-1 text-secondary-400 hover:text-secondary-600 dark:text-secondary-500 dark:hover:text-secondary-300 transition-colors',

        // White - for dark backgrounds
        white: 'p-2 text-white/80 hover:text-white rounded-full hover:bg-white/20 transition-colors',
    };

    return variants[props.variant] || variants.default;
});

// Icon size classes
const iconSize = computed(() => {
    const sizes = {
        sm: 'h-4 w-4',
        md: 'h-5 w-5',
        lg: 'h-6 w-6',
    };

    return sizes[props.size] || sizes.md;
});
</script>
